import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { MessageService } from 'primeng/api';
import { AuthenticationService } from '@servicios/authentication.service';
import { BranchService } from '@servicios/branch.service';
import { UserService } from '@servicios/user.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-stock-transfer-form',
  templateUrl: './stock-transfer-form.component.html',
  styleUrls: ['./stock-transfer-form.component.scss'],
  standalone: false
})
export class StockTransferFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private service = inject(InventoryService);
  private messageService = inject(MessageService);
  private authService = inject(AuthenticationService);
  private branchService = inject(BranchService);
  private userService = inject(UserService);

  mode: 'create' | 'approve' | 'receive' | 'view' = 'create';
  transferId: string | null = null;
  transferData: any = null;

  form!: FormGroup;
  loading = false;
  itemsOptions: any[] = [];
  allBranches: any[] = [];
  userBranches: any[] = [];
  sourceBranchesOptions: any[] = [];
  targetBranchesOptions: any[] = [];

  async ngOnInit() {
    this.mode = (this.route.snapshot.queryParams['mode'] as any) || 'create';
    this.transferId = this.route.snapshot.paramMap.get('id');
    this.userBranches = (this.authService.currentUserValue as any)?.branches || [];
    
    this.initForm();
    await this.loadAllBranches();

    if (this.mode !== 'create' && this.transferId) {
      this.loadTransfer();
    } else {
      // items will be loaded when sourceBranchId changes
      if (this.form.get('sourceBranchId')?.value) {
        this.loadItems(this.form.get('sourceBranchId')?.value);
      }
    }
  }

  async loadAllBranches() {
    try {
      const res = await firstValueFrom(this.branchService._getAll());
      this.allBranches = res.data?.branches || [];
      
      const authBranches = (this.authService.currentUserValue as any)?.branches || [];
      if (authBranches.length > 0 && typeof authBranches[0] === 'string') {
        this.userBranches = this.allBranches.filter(b => authBranches.includes(b.name));
      } else if (authBranches.length > 0 && authBranches[0].id) {
        this.userBranches = authBranches;
      } else {
        this.userBranches = [...this.allBranches];
      }
      
      this.updateBranchOptions(this.form?.get('isSpontaneous')?.value || false);
    } catch (e) {
      console.error('Error fetching branches', e);
      this.allBranches = [...this.userBranches];
      this.updateBranchOptions(this.form?.get('isSpontaneous')?.value || false);
    }
  }

  updateBranchOptions(isSpontaneous: boolean) {
    if (isSpontaneous) {
      this.sourceBranchesOptions = [...this.userBranches];
      this.targetBranchesOptions = [...this.allBranches];
    } else {
      this.sourceBranchesOptions = [...this.allBranches];
      this.targetBranchesOptions = [...this.userBranches];
    }
  }

  initForm() {
    this.form = this.fb.group({
      sourceBranchId: [{ value: null, disabled: this.mode !== 'create' }, Validators.required],
      targetBranchId: [{ value: null, disabled: this.mode !== 'create' }, Validators.required],
      isSpontaneous: [{ value: false, disabled: this.mode !== 'create' }],
      shipping_method: [{ value: '', disabled: this.mode === 'view' }],
      estimated_arrival: [{ value: null, disabled: this.mode === 'view' }],
      tracking_info: [{ value: '', disabled: this.mode === 'view' }],
      notes: [{ value: '', disabled: this.mode === 'view' }],
      loss_reason: [{ value: '', disabled: this.mode === 'view' }],
      items: this.fb.array([])
    }, { validators: this.differentBranchesValidator });

    if (this.mode === 'create') {
      this.form.get('isSpontaneous')?.valueChanges.subscribe(val => {
        this.updateBranchOptions(val);
        // Clear selections if they are no longer valid
        this.form.patchValue({ sourceBranchId: null, targetBranchId: null }, { emitEvent: false });
        this.itemsOptions = [];
        this.itemsArray.clear();
      });

      this.form.get('sourceBranchId')?.valueChanges.subscribe(branchId => {
        if (branchId) {
          this.loadItems(branchId);
        } else {
          this.itemsOptions = [];
          this.itemsArray.clear();
        }
      });
    }
  }

  differentBranchesValidator(control: AbstractControl): ValidationErrors | null {
    const source = control.get('sourceBranchId')?.value;
    const target = control.get('targetBranchId')?.value;
    if (source && target && source === target) {
      return { sameBranch: true };
    }
    return null;
  }

  get itemsArray() {
    return this.form.get('items') as FormArray;
  }

  get hasLoss(): boolean {
    if (this.mode !== 'receive') return false;
    return this.itemsArray.controls.some(c => {
      const disp = c.get('dispatched_quantity')?.value || 0;
      const rec = c.get('received_quantity')?.value || 0;
      return rec < disp;
    });
  }

  async loadItems(branchId?: string) {
    try {
      const params: any = { limit: 1000 };
      if (branchId) {
        params.branchId = branchId;
        const branch = this.allBranches.find(b => b.id === branchId);
        if (branch?.is_main) {
          params.includeUnassigned = true;
        }
      }
      const res = await this.service.getAll('items', params);
      const items = res.data || res;
      
      // Filtrar items que tengan stock disponible en la sucursal de origen
      this.itemsOptions = items.filter((i: any) => {
        const available = (i.stock || 0) - (i.reserved_stock || 0) - (i.blocked_stock || 0);
        return available > 0;
      }).map((i: any) => {
        const available = (i.stock || 0) - (i.reserved_stock || 0) - (i.blocked_stock || 0);
        return {
          ...i,
          name: `[${i.internal_code || 'S/C'}] ${i.name} (Disp: ${available} | Total: ${i.stock ?? 0})`
        };
      });
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los artículos' });
    }
  }

  async loadTransfer() {
    this.loading = true;
    try {
      this.transferData = await this.service.getOne('stock-transfers', this.transferId!);
      
      this.form.patchValue({
        sourceBranchId: this.transferData.sourceBranchId,
        targetBranchId: this.transferData.targetBranchId,
        shipping_method: this.transferData.shipping_method,
        estimated_arrival: this.transferData.estimated_arrival ? new Date(this.transferData.estimated_arrival) : null,
        tracking_info: this.transferData.tracking_info,
        notes: this.transferData.notes,
      });

      // Load items for display
      // We need item names, so let's load all items if not loaded
      if (this.itemsOptions.length === 0) {
        await this.loadItems();
      }

      this.transferData.items.forEach((item: any) => {
        const itemObj = this.itemsOptions.find((i: any) => i.id === item.itemId);
        
        const group = this.fb.group({
          itemId: [{ value: item.itemId, disabled: true }, Validators.required],
          itemName: [{ value: itemObj?.name || 'Desconocido', disabled: true }],
          requested_quantity: [{ value: item.requested_quantity, disabled: true }],
          dispatched_quantity: [{ 
            value: item.dispatched_quantity || item.requested_quantity, 
            disabled: this.mode !== 'approve' 
          }],
          received_quantity: [{ 
            value: item.received_quantity || item.dispatched_quantity || 0, 
            disabled: this.mode !== 'receive' 
          }]
        });
        
        this.itemsArray.push(group);
      });

      // Load user names for history
      try {
        const usersReq = await firstValueFrom(this.userService.getUsersList());
        const users = usersReq?.data?.usersList || [];
        if (this.transferData.requestedById) {
          const user = users.find((u: any) => u.id === this.transferData.requestedById);
          if (user) this.transferData.requestedBy_name = `${user.name} ${user.last_name || ''}`.trim();
        }
        if (this.transferData.dispatchedById) {
          const user = users.find((u: any) => u.id === this.transferData.dispatchedById);
          if (user) this.transferData.dispatchedBy_name = `${user.name} ${user.last_name || ''}`.trim();
        }
        if (this.transferData.receivedById) {
          const user = users.find((u: any) => u.id === this.transferData.receivedById);
          if (user) this.transferData.receivedBy_name = `${user.name} ${user.last_name || ''}`.trim();
        }
      } catch (err) {
        console.error('Error loading users', err);
      }

    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la transferencia' });
      this.location.back();
    } finally {
      this.loading = false;
    }
  }

  addItem() {
    this.itemsArray.push(this.fb.group({
      itemId: [null, Validators.required],
      requested_quantity: [1, [Validators.required, Validators.min(1)]]
    }));
  }

  removeItem(index: number) {
    this.itemsArray.removeAt(index);
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const data = this.form.getRawValue();

    try {
      if (this.mode === 'create') {
        const sourceBranchName = this.allBranches.find(b => b.id === data.sourceBranchId)?.name;
        const targetBranchName = this.allBranches.find(b => b.id === data.targetBranchId)?.name;
        
        const payload = { ...data, sourceBranchName, targetBranchName };
        delete payload.loss_reason;
        
        await this.service.create('stock-transfers', payload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Transferencia creada' });
      } else if (this.mode === 'approve') {
        const sourceBranchName = this.allBranches.find(b => b.id === data.sourceBranchId)?.name;
        const targetBranchName = this.allBranches.find(b => b.id === data.targetBranchId)?.name;
        const payload = {
          items: data.items.map((i: any) => ({ itemId: i.itemId, dispatched_quantity: Number(i.dispatched_quantity) })),
          shipping_method: data.shipping_method,
          estimated_arrival: data.estimated_arrival,
          tracking_info: data.tracking_info,
          notes: data.notes,
          sourceBranchName,
          targetBranchName
        };
        await this.service.approveStockTransfer(this.transferId!, payload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Transferencia despachada' });
      } else if (this.mode === 'receive') {
        const sourceBranchName = this.allBranches.find(b => b.id === data.sourceBranchId)?.name;
        const targetBranchName = this.allBranches.find(b => b.id === data.targetBranchId)?.name;
        
        if (this.hasLoss && !data.loss_reason) {
          this.messageService.add({ severity: 'error', summary: 'Motivo requerido', detail: 'Debe ingresar un motivo para la merma reportada.' });
          this.loading = false;
          return;
        }

        const payload = {
          items: data.items.map((i: any) => ({ itemId: i.itemId, received_quantity: Number(i.received_quantity) })),
          notes: data.notes,
          loss_reason: data.loss_reason,
          sourceBranchName,
          targetBranchName
        };
        await this.service.receiveStockTransfer(this.transferId!, payload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Transferencia recibida' });
      }
      this.router.navigate(['/inventory/transfers']);
    } catch (e: any) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: e?.error?.message || 'Ocurrió un error' });
    } finally {
      this.loading = false;
    }
  }

  async reject() {
    if (this.mode !== 'approve') return;
    
    this.loading = true;
    try {
      await this.service.rejectStockTransfer(this.transferId!, { notes: this.form.get('notes')?.value });
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Transferencia rechazada' });
      this.router.navigate(['/inventory/transfers']);
    } catch (e: any) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: e?.error?.message || 'Ocurrió un error' });
    } finally {
      this.loading = false;
    }
  }

  close() {
    this.router.navigate(['/inventory/transfers']);
  }
}
